<template>
  <div
    class="modal fade"
    id="formSummaryStockProgressModal"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
    data-backdrop="static"
    data-keyboard="false"
    ref="modalRoot"
  >
    <div class="modal-dialog modal-lg" role="document">
      <!-- FS-031 — ติดขัดกลางทางแล้ว popup ต้องเปลี่ยนเป็น "Popup Error (พื้นส้ม)" ตาม spec §M -->
      <div class="modal-content" :class="{ 'fss-progress--error': isErrorMode }">
        <div class="modal-header">
          <h5 class="modal-title">
            <i :class="isErrorMode ? 'fas fa-exclamation-triangle' : 'fas fa-tasks'"></i>
            {{ displayTitle }}
          </h5>
          <!-- FS-126 (19/10/2026) — ตัวนับเวลา HH:MM:SS มุมขวาบน · เฉพาะงานที่ผู้เรียกขอ (showTimer)
               ประมวลผล · บันทึก · ZIP Excel · ZIP PDF — หยุดค้างตอนรอเลือกที่รายที่ติดขัด (Paused)
               แล้วเดินต่อเมื่อกด ประมวลผลใหม่ / ข้าม · งานจบแล้วค้างเวลารวมไว้ -->
          <span v-if="showTimer" class="fss-progress__timer" data-test="fss-progress-timer">{{ elapsedText }}</span>
          <!-- ไม่มีปุ่มกากบาท โดยตั้งใจ — ต้องกด "ยกเลิก" หรือรอจนจบเท่านั้น
               กันผู้ใช้ปิดหน้าต่างแล้วเข้าใจว่างานหยุดแล้ว ทั้งที่ยังทำอยู่เบื้องหลัง -->
        </div>

        <div class="modal-body">
          <!-- begin: แถบความคืบหน้า -->
          <div class="fss-progress__headline">
            <span class="fss-progress__counter" data-test="fss-progress-counter">
              {{ processedCount }} / {{ agentCount }}
            </span>
            <span class="fss-progress__percent" data-test="fss-progress-percent">
              {{ percent }}%
            </span>
          </div>

          <div class="progress fss-progress__bar">
            <div
              class="progress-bar"
              :class="barClass"
              role="progressbar"
              :style="{ width: percent + '%' }"
              :aria-valuenow="percent"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <!-- end: แถบความคืบหน้า -->

          <!-- begin: กำลังทำรายไหนอยู่ -->
          <div v-if="isRunning" class="fss-progress__current" data-test="fss-progress-current">
            <i class="fas fa-spinner fa-spin"></i>
            กำลังทำ:
            <strong>{{ currentLabel }}</strong>
          </div>
          <!-- end: กำลังทำรายไหนอยู่ -->

          <!-- begin: สรุปตัวเลข -->
          <div class="fss-progress__stats">
            <span class="fss-progress__stat fss-progress__stat--ok">
              <i class="fas fa-check-circle"></i> สำเร็จ {{ createdCount }}
            </span>
            <span class="fss-progress__stat fss-progress__stat--skip">
              <i class="fas fa-minus-circle"></i> ข้าม {{ skippedCount }}
            </span>
            <span
              class="fss-progress__stat fss-progress__stat--fail"
              v-if="failedCount > 0"
            >
              <i class="fas fa-times-circle"></i> ไม่สำเร็จ {{ failedCount }}
            </span>
          </div>
          <!-- end: สรุปตัวเลข -->

          <!-- begin: log รายตัวแทน -->
          <div class="fss-progress__log" data-test="fss-progress-log">
            <div
              v-for="item in displayItems"
              :key="item.key"
              class="fss-progress__row"
              :class="'fss-progress__row--' + item.tone"
            >
              <span class="fss-progress__icon">{{ item.icon }}</span>
              <span class="fss-progress__seq">{{ item.seqNo }}.</span>
              <span class="fss-progress__name">{{ item.label }}</span>
              <span class="fss-progress__note">{{ item.note }}</span>
            </div>

            <div v-if="displayItems.length === 0" class="fss-progress__empty">
              กำลังเริ่มต้น...
            </div>
          </div>
          <!-- end: log รายตัวแทน -->

          <!-- begin: FS-031 — กล่องข้อผิดพลาด (spec §M "Popup Error") -->
          <!--
            spec สั่งให้แสดง 3 อย่าง: สถิติ (สำเร็จกี่ราย จากทั้งหมดกี่ราย) ·
            ติดขัดที่ตัวแทนรายไหน · ปุ่มดูข้อมูลข้อความผิดพลาดโดยละเอียด
            เลขอ้างอิงขึ้นบนหน้าจอเลย (ไม่ซ่อนใน popup ย่อยอย่างเดียว) เพราะเป็นสิ่งที่
            ผู้ใช้ต้องอ่านให้ทีมดูแลระบบทางโทรศัพท์บ่อยที่สุด
          -->
          <div
            v-if="isErrorMode"
            class="fss-progress__error"
            data-test="fss-progress-error"
          >
            <div class="fss-progress__error-stat" data-test="fss-error-stats">
              <i class="fas fa-check-circle"></i>
              ประมวลผลสำเร็จแล้ว {{ successCount }} จากทั้งหมด {{ agentCount }} {{ unitLabel }}
            </div>

            <div class="fss-progress__error-agent" data-test="fss-error-agent">
              <i class="fas fa-times-circle"></i>
              ติดขัดที่: {{ failedLabel }}
            </div>

            <div class="fss-progress__error-ref">
              เลขอ้างอิง:
              <code data-test="fss-error-reference">{{ failedReference }}</code>
            </div>

            <button
              type="button"
              class="btn btn-sm fss-progress__error-detail-btn"
              data-test="fss-error-detail"
              @click="openErrorDetail"
            >
              <i class="fas fa-search"></i>
              ตรวจสอบข้อมูลข้อความผิดพลาด (สำหรับส่งทีมดูแลระบบ SBP)
            </button>
          </div>
          <!-- end: FS-031 — กล่องข้อผิดพลาด -->

          <!-- begin: FS-031 / FS-105 — 2 ทางเลือก (mockup §M "Popup Error")
               mockup ทำเป็นกล่องเต็มความกว้าง หัวข้อ + คำอธิบายใต้หัวข้อ ไม่ใช่ปุ่มในแถบล่าง
               เพราะทั้งสองทางให้ผลต่างกัน ผู้ใช้ต้องอ่านก่อนตัดสินใจ
               FS-105 — ใช้กับงานหลายตัวแทนทั้ง "ประมวลผล" และ "บันทึกข้อมูล" ที่หยุดรออยู่ (Paused)
               และทางเลือกที่ 2 เปลี่ยนจาก "ยกเลิกและลบไฟล์ที่สร้างแล้ว" เป็น "ข้าม" (ผู้ใช้สั่ง 15/10/2026) -->
          <template v-if="canChooseAfterFailure">
            <div class="fss-progress__error-choose">โปรดเลือกวิธีการดำเนินการต่อ:</div>

            <button
              type="button"
              class="fss-error-action fss-error-action--resume"
              data-test="fss-error-resume"
              :disabled="resuming || skipping"
              @click="resumeFromFailure"
            >
              <span class="fss-error-action__title">
                <i class="fas fa-redo"></i>
                {{ resuming ? "กำลังเริ่มต่อ..." : "ประมวลผลใหม่เริ่มจากตัวแทนจำหน่ายที่ติดขัด" }}
              </span>
              <span
                v-if="isDocumentJob"
                class="fss-error-action__desc"
                data-test="fss-error-resume-desc"
              >
                ระบบจะประมวลผลใหม่เริ่มจาก <strong>{{ failedLabel }}</strong> โดยไม่ต้องเริ่มใหม่ —
                <strong>{{ createdCount }} เอกสารที่สร้างสำเร็จแล้วจะคงอยู่</strong>
                ประมวลผลต่อจนครบ {{ agentCount }} เอกสาร
              </span>
              <span v-else class="fss-error-action__desc" data-test="fss-error-resume-desc">
                ระบบจะประมวลผลใหม่เริ่มจาก <strong>{{ failedLabel }}</strong> โดยไม่ต้องเริ่มใหม่ —
                <strong>ข้อมูล {{ successCount }} ตัวแทนจำหน่ายที่ประมวลผลสำเร็จแล้วจะคงอยู่</strong>
                ประมวลผลต่อจนครบ {{ agentCount }} ตัวแทนจำหน่าย
              </span>
            </button>

            <button
              type="button"
              class="fss-error-action fss-error-action--skip"
              data-test="fss-error-skip"
              :disabled="resuming || skipping"
              @click="skipFailedAgent"
            >
              <span class="fss-error-action__title">
                <i class="fas fa-forward"></i>
                {{ skipping ? "กำลังข้าม..." : "ข้ามการประมวลผลตัวแทนจำหน่ายที่ติดขัด" }}
              </span>
              <span class="fss-error-action__desc" data-test="fss-error-skip-desc">
                ระบบจะข้าม <strong>{{ failedLabel }}</strong> แล้ว<strong>เริ่มประมวลผลตัวแทนจำหน่ายรายถัดไป</strong> —
                ท่านต้องไปดำเนินการสร้างเอกสารเฉพาะตัวแทนจำหน่ายที่ติดขัดเอง
                หลังประมวลผลเอกสารในรอบนี้เสร็จ
                <template v-if="!isDocumentJob">
                  (รายที่ข้ามจะแสดงท้ายตารางในขั้นตอน "ตรวจสอบข้อมูล" และไม่ถูกนำไปสร้างเอกสาร)
                </template>
              </span>
            </button>
          </template>
          <!-- end: FS-031 / FS-105 — 2 ทางเลือก -->

          <!-- begin: ข้อความเมื่อจบงาน -->
          <div
            v-if="!isRunning && !isErrorMode && finishedMessage"
            class="alert fss-progress__result"
            :class="resultAlertClass"
            data-test="fss-progress-result"
          >
            {{ finishedMessage }}
          </div>
          <!-- end: ข้อความเมื่อจบงาน -->

          <!-- begin: คำเตือนเรื่องการยกเลิก (ข้อจำกัดที่ต้องบอกผู้ใช้ตรง ๆ) -->
          <div v-if="cancelRequested && isRunning" class="fss-progress__cancelling">
            <i class="fas fa-hourglass-half"></i>
            สั่งยกเลิกแล้ว — ระบบจะหยุดหลังจากเอกสารฉบับที่กำลังทำอยู่เสร็จ
          </div>
          <!-- end: คำเตือนเรื่องการยกเลิก -->
        </div>

        <!-- โหมดหยุดรอที่รายที่ติดขัด ไม่มีปุ่มในแถบล่าง (ต้องเลือก 1 ใน 2 ทางเท่านั้น) จึงซ่อนแถบไปเลย
             กัน modal-footer ว่าง ๆ ที่ยังกินพื้นที่และมีเส้นขอบบน -->
        <div class="modal-footer" v-if="isRunning || !canChooseAfterFailure">
          <button
            v-if="isRunning"
            type="button"
            class="btn btn-outline-danger"
            data-test="fss-progress-cancel"
            :disabled="cancelRequested"
            @click="requestCancel"
          >
            <i class="fas fa-stop"></i>
            {{ cancelRequested ? "กำลังหยุด..." : "ยกเลิก" }}
          </button>

          <!--
            F13 (J2) — งานที่ผลิตไฟล์ (ZIP) จะมีปุ่มดาวน์โหลดเพิ่มมา
            ใช้ปุ่มยิง axios ไม่ใช่ <a href> เพราะ endpoint ต้องการ Bearer token
            ซึ่งการ navigate ของเบราว์เซอร์ไม่แนบ header ให้ (จะได้ 401)
          -->
          <!--
            FS-031 — โหมดข้อผิดพลาดของงาน "สร้างเอกสารรวม" : spec ให้เลือก 2 ทางเท่านั้น
            จึงไม่มีปุ่ม "ปิด" ให้เดินหนีไปหน้าขั้นที่ 6 พร้อมเอกสารครึ่งชุด
            (งานอื่นที่ไม่ได้สร้างเอกสาร เช่น ประมวลผล/Export ยังใช้ปุ่ม "ปิด" ตามเดิม)
            ตัวเลือกทั้งสองย้ายไปอยู่ใน modal-body แล้ว เพราะ mockup ให้มีคำอธิบายใต้หัวข้อ
          -->
          <template v-if="!isRunning && !canChooseAfterFailure">
            <button
              v-if="hasResultFile"
              type="button"
              class="btn btn-success kt-margin-r-10"
              data-test="fss-progress-download"
              :disabled="downloading"
              @click="downloadResult"
            >
              <i class="fas fa-download"></i>
              {{ downloading ? "กำลังดาวน์โหลด..." : "ดาวน์โหลดไฟล์" }}
            </button>

            <button
              type="button"
              class="btn"
              :class="hasResultFile ? 'btn-secondary' : 'btn-primary'"
              data-test="fss-progress-close"
              @click="close"
            >
              ปิด
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F14 (K1) — Popup แสดงความคืบหน้าของงานที่ใช้เวลานาน
//
// spec ระบุว่าใช้กับ 5 งาน: ประมวลผล (2→3/3B) / บันทึกข้อมูล (3B→6) /
// Export Excel ตรวจ / ZIP Excel / ZIP PDF
// component นี้จึงไม่ผูกกับงานใดงานหนึ่ง — ผู้เรียกส่ง "endpoint" เข้ามา
//   start(payload)  -> คืน { batchId }
//   progress(id)    -> คืนสถานะ
//   cancel(id)      -> สั่งหยุด
//   download(id)    -> เฉพาะงานที่ผลิตไฟล์
//   resume          -> ปุ่มบน Popup Error ของงานหลายตัวแทน (FS-031 / FS-105)
//                      ส่ง { batchId, skipFailed } — false = ประมวลผลใหม่จากรายที่ติดขัด · true = ข้าม
//
// FS-033 (26/09/2026) — ต่อครบทั้ง 5 กรณีแล้ว:
//   FormSummaryStockCreate.vue      : ประมวลผล (ProcessStart / GroupProcessStart)
//   FormSummaryStockGroupPreview.vue: บันทึกข้อมูล (GroupSaveStart) · Export Excel ตรวจ
//   FormSummaryStockGroupResult.vue : ZIP Excel · ZIP PDF
//
// FS-031 (26/09/2026) — ติดขัดกลางทางแล้ว popup เปลี่ยนเป็น "Popup Error (พื้นส้ม)"
//   พร้อมสถิติ · รายที่ติดขัด · ปุ่มดูรายละเอียด (สาเหตุ+เวลา+เลขอ้างอิง) ·
//   และ 2 ทางเลือกตาม spec §M (ประมวลผลต่อจากรายที่ติดขัด / ลบเอกสารที่สร้างแล้ว)
//
// FS-105 (15/10/2026) — ผู้ใช้สั่งปรับ (งานหลายตัวแทนทั้ง "ประมวลผล" และ "บันทึกข้อมูล"):
//   • งานหยุดรอที่รายที่ติดขัดทันที (สถานะ Paused) ไม่ใช่ทำต่อจนครบแล้วค่อยถาม
//   • ทางเลือกที่ 2 เปลี่ยนเป็น "ข้ามการประมวลผลตัวแทนจำหน่ายที่ติดขัด" แล้วทำรายถัดไป
//     (ไม่ลบอะไร — ผู้ใช้ไปสร้างเอกสารของรายที่ติดขัดเองภายหลัง)
//   • ติดขัดอีกรายในรอบต่อไป popup ก็ขึ้นถามอีกครั้ง
//
// ── ทำไมเป็น polling ────────────────────────────────────────────────────────
//   โปรเจกต์นี้ไม่มี SignalR/WebSocket (ตรวจแล้ว) การเพิ่มเข้ามาเพื่อ CR เดียว
//   กระทบ infra ทั้งระบบ — polling คือทางที่ตรงกับของเดิมที่สุด
//
// ── สิ่งที่ปุ่มยกเลิกทำจริง (FS-028) ────────────────────────────────────────
//   กดยกเลิกแล้วระบบ "หยุดไม่สร้างใบต่อไป" + "ลบเอกสารที่สร้างไปแล้วทั้งชุดทิ้ง"
//   (งาน H3 ทำเสร็จแล้ว — RollbackBatchAsync soft delete หัวเอกสารทุกใบของ BatchId นั้น)
//   เป็นการลบที่ผู้ใช้กู้คืนเองไม่ได้ ข้อความยืนยันจึงต้องบอกตรง ๆ ก่อนกด
//
// SweetAlert2 = ^8.2.3 → ใช้ option ชื่อ `type` และอ่านผลจาก `result.value`
// ─────────────────────────────────────────────────────────────────────────────

// ถี่เกินไปจะยิง API รัว ห่างเกินไปแถบจะกระตุก — 1.5 วินาทีพอดีกับงานที่ใช้เวลาหลายนาที
const POLL_INTERVAL_MS = 1500;

// จำนวนครั้งที่ยอมให้ poll ไม่สำเร็จติดกันก่อนยอมแพ้ (เน็ตสะดุดชั่วคราวไม่ควรล้มทั้งงาน)
const MAX_CONSECUTIVE_ERRORS = 5;

// FS-082 — ตัวแทนแสดงเป็น "[รหัส] ชื่อ" แบบเดียวกับหน้าอื่นในโมดูล (หน้ารายการ · ขั้นที่ 6 · Filter ที่เลือก)
//   ใช้ร่วมกันทั้งบรรทัด "กำลังทำ:" · รายการที่เสร็จแล้ว · popup error ให้ทั้ง popup เป็นรูปแบบเดียว
//   (ต่างจากตัวอย่างในรายงาน QA เรื่องที่ 10 ที่ไม่มีวงเล็บ — ผู้ใช้สั่ง 14/10/2026)
function agentLabel(code, name) {
  if (!code) return name || "";
  return name ? "[" + code + "] " + name : "[" + code + "]";
}

export default {
  data: function() {
    return {
      title: "กำลังดำเนินการ",
      batchId: null,

      agentCount: 0,
      processedCount: 0,
      createdCount: 0,
      skippedCount: 0,
      failedCount: 0,
      percent: 0,

      currentAgentCode: "",
      currentAgentName: "",

      state: "Running",
      finishedMessage: "",
      items: [],

      cancelRequested: false,
      consecutiveErrors: 0,
      timer: null,

      // F13 (J2) — งานที่ผลิตไฟล์ (เช่น ZIP) จะตั้ง 2 ค่านี้เมื่อจบ
      hasResultFile: false,
      resultFileName: "",
      downloading: false,

      // ── FS-033 : ชนิดของงาน ───────────────────────────────────────────────
      //   "document" = สร้างเอกสารรวม (ยกเลิกแล้วลบเอกสาร · มีปุ่มประมวลผลใหม่/ข้าม เมื่อติดขัด)
      //   "file"     = ผลิตไฟล์ให้ดาวน์โหลด (ZIP / Excel ตรวจ) — ยกเลิกแล้วทิ้งแค่ไฟล์
      //   "process"  = ประมวลผลเพื่อแสดงผลบนหน้าจอ — ยังไม่บันทึกอะไรลงระบบเลย
      //   ผู้เรียกส่งมาทาง start() · ถ้าไม่ส่งจะเดาจาก endpoint (ดู computed jobKindResolved)
      jobKind: "",

      // FS-033 — หน่วงการเปิด popup กี่มิลลิวินาที (0 = เปิดทันที)
      //   ใช้กับงานที่ "อาจจะ" สั้นมาก เช่นประมวลผลตัวแทนรายเดียวที่มีบาร์โค้ดไม่กี่ร้อยแถว
      //   งานที่จบก่อนครบเวลานี้จะไม่เห็น popup แวบขึ้นมาเลย
      deferMs: 0,
      deferTimer: null,
      isShown: false,

      // FS-110 — งานจบครบแล้วปิด popup เองหลังค้างไว้กี่ ms (0 = ไม่ปิดเอง รอผู้ใช้กด "ปิด")
      //   ผู้เรียกส่งมาทาง start() · ตอนนี้ใช้แค่งานสร้างเอกสารรวม (spec: "สำเร็จครบ → ปิด popup อัตโนมัติ")
      autoCloseMs: 0,
      autoCloseTimer: null,

      // FS-126 — ตัวนับเวลา : เวลาที่สะสมแล้ว (ms) + จุดเริ่มของช่วงที่กำลังเดิน (null = หยุดอยู่)
      showTimer: false,
      timerElapsedMs: 0,
      timerRunningSince: null,
      timerNow: 0,
      timerTick: null,

      // FS-031 / FS-105 — สถานะของ 2 ปุ่มบน popup error
      resuming: false,
      skipping: false,

      // endpoint ที่ผู้เรียกกำหนด — ดูหมายเหตุที่หัวไฟล์
      endpoints: {
        start: "/Api/FormSummaryStock/GroupSaveStart",
        progress: "/Api/FormSummaryStock/GroupProgress",
        cancel: "/Api/FormSummaryStock/GroupCancel",

        // F13 (J2) — ใช้เฉพาะงานที่ผลิตไฟล์ · {jobId} จะถูกแทนด้วย batchId ของงานนั้น
        download: "/Api/FormSummaryStock/GroupZipDownload?jobId={jobId}",

        // FS-031 / FS-105 — 2 ทางเลือกบน popup error (ประมวลผลใหม่ / ข้าม) ใช้ endpoint เดียวกัน
        //   งาน "ประมวลผล" หลายตัวแทนส่ง /GroupProcessResume มาทับ (FormSummaryStockCreate.vue)
        resume: "/Api/FormSummaryStock/GroupSaveResume",
      },
    };
  },
  computed: {
    isRunning: function() {
      return this.state === "Running";
    },

    // FS-126 — HH:MM:SS ของเวลาที่งานวิ่งจริง (ไม่นับช่วงหยุดรอผู้ใช้)
    elapsedText: function() {
      let ms = this.timerElapsedMs;
      if (this.timerRunningSince !== null) ms += Math.max(0, this.timerNow - this.timerRunningSince);

      const total = Math.floor(ms / 1000);
      const pad = function(n) {
        return n < 10 ? "0" + n : String(n);
      };
      return pad(Math.floor(total / 3600)) + ":" + pad(Math.floor((total % 3600) / 60)) + ":" + pad(total % 60);
    },

    // ── FS-033 : ชนิดของงาน ─────────────────────────────────────────────────
    //
    //   ผู้เรียกควรส่ง jobKind มาทาง start() แต่ต้องเดาจาก endpoint ได้ด้วย
    //   เพราะมีเทสที่เรียก requestCancel() ตรง ๆ โดยตั้งแค่ endpoints.start
    //   (formsummarystock-groupresult.spec.js GR-8 / GR-9)
    jobKindResolved: function() {
      if (this.jobKind) return this.jobKind;

      const start = this.endpoints.start || "";

      // งานสร้างเอกสารรวมเท่านั้นที่ยกเลิกแล้วลบเอกสารทิ้ง (H3 / RollbackBatchAsync)
      if (start.indexOf("GroupSaveStart") !== -1) return "document";
      if (start.indexOf("ProcessStart") !== -1) return "process";

      return "file";
    },
    isDocumentJob: function() {
      return this.jobKindResolved === "document";
    },

    // ── FS-031 : โหมดข้อผิดพลาด ─────────────────────────────────────────────
    //
    //   spec §M : "ถ้าระบบติดขัดที่ตัวแทนจำหน่ายรายใดรายหนึ่ง — Popup Progress
    //   จะเปลี่ยนเป็น Popup Error" → เงื่อนไขคือ "มีรายที่ล้มเหลว" ไม่ใช่
    //   "ทั้งงานล้มเหลว" เพราะ handler ตั้งใจให้รายเดียวพังแล้วชุดยังเดินต่อได้
    //   (งานที่ล้มทั้งก้อนใช้ alert เดิม — ไม่มีรายที่ติดขัดให้ชี้)
    //
    //   ⚠ ชุดที่ "ถูกยกเลิก" ไม่ใช่ "ติดขัดกลางทาง" — ต้องไม่เข้าโหมดนี้
    //     ใบที่กำลังบันทึกอยู่ตอนผู้ใช้กดยกเลิกจะถูกบันทึกเป็น Failed ด้วย
    //     (SaveFormSummaryStockCommandHandler คืนผลว่าไม่สำเร็จเมื่อ token ถูกยกเลิก)
    //     ถ้านับเป็น error ผู้ใช้จะโดนถาม "จะทำต่อหรือจะลบ" ทั้งที่เพิ่งสั่งยกเลิกเอง
    //     และเอกสารทั้งชุดถูกลบไปแล้วตั้งแต่ตอนยกเลิก (H3) ไม่มีอะไรให้เลือกอีก
    isErrorMode: function() {
      if (this.isRunning) return false;
      if (this.state === "Cancelled") return false;
      return this.failedItem !== null;
    },
    // FS-105 — รายที่ผู้ใช้กด "ข้าม" ไปแล้วถือว่าตัดสินแล้ว ไม่นับเป็นรายที่ติดขัดอยู่
    failedItem: function() {
      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        if (item.status === "Failed" && !item.isSkippedByUser) return item;
      }
      return null;
    },

    // FS-105 — งานหยุดรออยู่ที่รายที่ติดขัด = ต้องให้ผู้ใช้เลือก 1 ใน 2 ทาง (ไม่มีปุ่มปิด)
    canChooseAfterFailure: function() {
      return this.state === "Paused" && this.isErrorMode;
    },

    // FS-105 — "ประมวลผลสำเร็จแล้ว N จากทั้งหมด M ..."
    //   งานบันทึก = นับเอกสารที่สร้างได้ (ตาม mockup "34 จากทั้งหมด 55 เอกสาร")
    //   งานประมวลผล = นับตัวแทนที่เรียกข้อมูลได้แล้ว ทั้งที่มีและไม่มีสินค้าคงค้าง (ยังไม่มีเอกสาร)
    successCount: function() {
      if (this.isDocumentJob) return this.createdCount;
      return this.createdCount + this.skippedCount;
    },
    unitLabel: function() {
      return this.isDocumentJob ? "เอกสาร" : "ตัวแทนจำหน่าย";
    },
    failedLabel: function() {
      const item = this.failedItem;
      if (!item) return "-";
      return agentLabel(item.agentCode, item.agentName);
    },
    failedReference: function() {
      const item = this.failedItem;
      return item && item.errorReference ? item.errorReference : "-";
    },
    displayTitle: function() {
      if (!this.isErrorMode) return this.title;
      return "พบข้อผิดพลาดที่ตัวแทนจำหน่ายรายที่ " + this.failedItem.seqNo;
    },
    barClass: function() {
      if (this.state === "Cancelled") return "bg-warning";
      if (this.state === "Failed") return "bg-danger";
      if (this.failedCount > 0) return "bg-warning";
      return this.isRunning ? "progress-bar-striped progress-bar-animated" : "bg-success";
    },
    resultAlertClass: function() {
      if (this.state === "Cancelled") return "alert-warning";
      if (this.state === "Failed") return "alert-danger";
      return this.failedCount > 0 ? "alert-warning" : "alert-success";
    },
    currentLabel: function() {
      return agentLabel(this.currentAgentCode, this.currentAgentName) || "-";
    },
    // แปลงรายการจาก API ให้พร้อมวาด แล้วเรียงล่าสุดไว้บน (ผู้ใช้สนใจตัวที่เพิ่งจบ)
    displayItems: function() {
      const tones = { Created: "ok", Skipped: "skip", Failed: "fail" };
      const icons = { Created: "✔", Skipped: "–", Failed: "✕" };

      return this.items
        .slice()
        .reverse()
        .map(function(item) {
          return {
            key: item.seqNo + "-" + item.agentCode,
            seqNo: item.seqNo,
            label: agentLabel(item.agentCode, item.agentName),
            tone: tones[item.status] || "skip",
            icon: icons[item.status] || "•",
            // FS-105 — รายที่ติดขัดแล้วผู้ใช้กดข้าม บอกให้ชัดว่าข้ามไปแล้ว ไม่ได้ค้างอยู่
            note: item.isSkippedByUser
              ? (item.message || "ติดขัด") + " · ข้ามแล้ว"
              : item.documentNo || item.message || "",
          };
        });
    },
  },
  watch: {
    // FS-126 — เดินเฉพาะตอน Running · ออกจาก Running (Paused / จบ) = หยุดสะสม · กลับมา Running = เดินต่อ
    state: function(value) {
      if (!this.showTimer) return;
      if (value === "Running") this.resumeElapsedTimer();
      else this.pauseElapsedTimer();
    },
  },
  methods: {
    // ── FS-126 : ตัวนับเวลา ──────────────────────────────────────────────────
    resumeElapsedTimer: function() {
      const self = this;
      if (self.timerRunningSince !== null) return;

      self.timerRunningSince = Date.now();
      self.timerNow = self.timerRunningSince;
      if (!self.timerTick) {
        self.timerTick = setInterval(function() {
          self.timerNow = Date.now();
        }, 1000);
      }
    },
    pauseElapsedTimer: function() {
      if (this.timerRunningSince !== null) {
        this.timerElapsedMs += Math.max(0, Date.now() - this.timerRunningSince);
        this.timerRunningSince = null;
      }
      if (this.timerTick) {
        clearInterval(this.timerTick);
        this.timerTick = null;
      }
    },
    resetElapsedTimer: function() {
      this.pauseElapsedTimer();
      this.timerElapsedMs = 0;
      this.timerNow = 0;
    },

    /**
     * เริ่มงานใหม่แล้วเปิด popup
     *   options.title     ชื่องานที่แสดงบนหัว popup
     *   options.payload   body ที่ส่งไปให้ endpoint start
     *   options.endpoints ทับค่า endpoint เริ่มต้น (ถ้างานอื่นใช้คนละชุด)
     *   options.jobKind   "document" | "file" | "process" (FS-033 — ดู jobKindResolved)
     *   options.deferMs   FS-033 — หน่วงการเปิด popup กี่ ms (0/ไม่ส่ง = เปิดทันที)
     *   options.autoCloseMs FS-110 — งานจบครบแล้วปิด popup เองหลังกี่ ms (0/ไม่ส่ง = รอกด "ปิด")
     *   options.showTimer FS-126 — true = แสดงตัวนับเวลา HH:MM:SS มุมขวาบน (เริ่มนับตอนเรียก start)
     */
    start: function(options) {
      const self = this;
      const opts = options || {};

      self.reset();
      self.title = opts.title || "กำลังดำเนินการ";
      self.jobKind = opts.jobKind || "";
      self.deferMs = opts.deferMs || 0;
      self.autoCloseMs = opts.autoCloseMs || 0;

      // FS-126 — เริ่มนับตั้งแต่ผู้ใช้กดเริ่มงาน
      self.showTimer = opts.showTimer === true;
      if (self.showTimer) self.resumeElapsedTimer();

      if (opts.endpoints) {
        self.endpoints = Object.assign({}, self.endpoints, opts.endpoints);
      }

      // FS-033 — งานที่ "อาจจะ" สั้นมากให้หน่วงไว้ก่อน จะได้ไม่มี popup กระพริบ
      //   งานที่ยาวจริงจะถูกเปิดเมื่อครบเวลา ส่วนงานที่จบก่อนจะไม่เปิดเลย
      //   (ยกเว้นงานที่จบแบบผิดพลาด — finish() จะบังคับเปิดให้ผู้ใช้เห็นเสมอ)
      if (self.deferMs > 0) {
        self.deferTimer = setTimeout(function() {
          self.deferTimer = null;
          if (self.isRunning) self.showModal();
        }, self.deferMs);
      } else {
        self.showModal();
      }

      axios
        .post(self.endpoints.start, opts.payload || {}, {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function(response) {
          const data = (response && response.data) || {};

          if (!data.success) {
            self.state = "Failed";
            self.finishedMessage = data.message || "เริ่มงานไม่สำเร็จ";
            return;
          }

          self.batchId = data.batchId;
          self.agentCount = data.agentCount || 0;
          self.scheduleNextPoll();
        })
        .catch(function() {
          self.state = "Failed";
          self.finishedMessage = "เริ่มงานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
        });
    },

    // ── polling ───────────────────────────────────────────────────────────
    scheduleNextPoll: function() {
      const self = this;
      self.clearTimer();
      self.timer = setTimeout(function() {
        self.poll();
      }, POLL_INTERVAL_MS);
    },

    poll: function() {
      const self = this;

      if (!self.batchId || !self.isRunning) return;

      axios
        .post(
          self.endpoints.progress,
          { batchId: self.batchId },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};

          if (!data.success) {
            // ไม่พบชุดนี้แล้ว — แอปอาจถูก restart ระหว่างทาง (เช่น deploy)
            // FS-120 — บอกผู้ใช้ว่าต้องทำอะไรต่อ แทนข้อความจาก server ("ไม่พบข้อมูลความคืบหน้า...")
            self.state = "Failed";
            self.finishedMessage = self.serverLostMessage();
            self.finish();
            return;
          }

          self.consecutiveErrors = 0;
          self.applyProgress(data);

          if (data.isRunning) {
            self.scheduleNextPoll();
          } else {
            self.finish();
          }
        })
        .catch(function() {
          // เน็ตสะดุดชั่วคราวไม่ควรล้มทั้งงาน — ลองใหม่จนกว่าจะเกินโควตา
          self.consecutiveErrors++;

          if (self.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            self.state = "Failed";
            // FS-120 — งานประมวลผลยังไม่บันทึกอะไร ประมวลผลใหม่ได้เลย จึงใช้ข้อความเดียวกับกรณี server รีสตาร์ท
            //   งานบันทึก / สร้างไฟล์ คงข้อความเดิม — งานอาจยังทำอยู่เบื้องหลัง กดซ้ำแล้วอาจได้เอกสารซ้ำ
            self.finishedMessage =
              self.jobKindResolved === "process"
                ? self.serverLostMessage()
                : "ขาดการติดต่อกับระบบ งานอาจยังทำงานอยู่เบื้องหลัง " +
                  "กรุณาตรวจสอบที่หน้ารายการเอกสาร";
            self.finish();
            return;
          }

          self.scheduleNextPoll();
        });
    },

    // FS-120 — ข้อความเมื่อ Web Server มีปัญหาระหว่างทาง (รีสตาร์ท / deploy) แล้วงานหายจากหน่วยความจำ
    //   ปุ่ม "ประมวลผลใหม่ / ข้าม" ของ FS-105 ใช้ไม่ได้ เพราะ server ไม่มีงานนี้แล้ว → ให้ผู้ใช้เริ่มใหม่เอง
    //   งานบันทึก : เอกสารบางส่วนอาจสร้างไปแล้ว ต้องตรวจก่อน ไม่งั้นกดบันทึกซ้ำได้เอกสารซ้ำ
    serverLostMessage: function() {
      if (this.isDocumentJob) {
        return (
          "Web Server มีปัญหาในระหว่างบันทึกข้อมูล เอกสารบางส่วนอาจถูกสร้างไปแล้ว " +
          "กรุณาปิดหน้าต่างแล้วตรวจสอบที่หน้ารายการเอกสารก่อนบันทึกข้อมูลอีกครั้ง"
        );
      }
      if (this.jobKindResolved === "file") {
        return "Web Server มีปัญหาในระหว่างสร้างไฟล์ กรุณาปิดหน้าต่างแล้วกดสร้างไฟล์อีกครั้ง";
      }
      return "Web Server มีปัญหาในระหว่างประมวลผล กรุณาปิดหน้าต่างแล้วกดประมวลผลอีกครั้ง";
    },

    applyProgress: function(data) {
      this.state = data.state || "Running";
      this.percent = data.percent || 0;
      this.agentCount = data.agentCount || this.agentCount;
      this.processedCount = data.processedCount || 0;
      this.createdCount = data.createdCount || 0;
      this.skippedCount = data.skippedCount || 0;
      this.failedCount = data.failedCount || 0;
      this.currentAgentCode = data.currentAgentCode || "";
      this.currentAgentName = data.currentAgentName || "";
      this.items = data.items || [];

      // F13 (J2) — งานที่ผลิตไฟล์จะส่ง 2 ค่านี้มาด้วย งานอื่นจะเป็น false/null
      this.hasResultFile = data.hasResultFile === true;
      this.resultFileName = data.resultFileName || "";

      if (!data.isRunning) {
        this.finishedMessage = data.message || "";
      }
    },

    // FS-033 — เปิด popup จริง (แยกออกมาเพราะ deferMs ทำให้เปิดได้ 2 จังหวะ)
    showModal: function() {
      if (this.isShown) return;

      this.clearDeferTimer();
      this.isShown = true;

      if (this.$refs.modalRoot) {
        $(this.$refs.modalRoot).modal("show");
      }

      this.$emit("shown", { batchId: this.batchId });
    },

    clearDeferTimer: function() {
      if (this.deferTimer) {
        clearTimeout(this.deferTimer);
        this.deferTimer = null;
      }
    },

    clearAutoCloseTimer: function() {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
    },

    // FS-110 — ปิดเองได้เมื่อ งานจบ Completed · ไม่มีรายที่ติดขัดค้างอยู่ · มีเอกสารให้ไปดูที่ขั้นที่ 6
    //   รายที่ผู้ใช้กด "ข้าม" (FS-105) แล้วทำต่อจนจบ = ผ่านเงื่อนไข (ผู้ใช้ตัดสินใจแล้ว — ผู้ใช้ยืนยัน 16/10/2026)
    //   ยกเลิก / Failed / หยุดรอที่รายที่ติดขัด → ไม่ปิดเอง
    canAutoClose: function() {
      return this.autoCloseMs > 0 &&
        this.state === "Completed" &&
        !this.isErrorMode &&
        this.createdCount > 0;
    },

    finish: function() {
      this.clearTimer();
      this.clearDeferTimer();

      // FS-033/FS-031 — งานที่ถูกหน่วงไว้แล้วจบแบบไม่เรียบร้อย ต้องบังคับให้ popup โผล่
      //   ไม่งั้นผู้ใช้จะไม่เห็นทั้งข้อความผิดพลาดและปุ่มแก้ไขสถานการณ์เลย
      if (!this.isShown && (this.state !== "Completed" || this.failedCount > 0)) {
        this.showModal();
      }

      this.$emit("finished", {
        batchId: this.batchId,
        state: this.state,
        jobKind: this.jobKindResolved,
        createdCount: this.createdCount,
        skippedCount: this.skippedCount,
        failedCount: this.failedCount,
      });

      // FS-110 — ค้างไว้ให้เห็น 100% ก่อน แล้วปิดเองเหมือนผู้ใช้กด "ปิด" (→ ผู้เรียกพาไปขั้นที่ 6)
      if (this.canAutoClose()) {
        const self = this;
        self.clearAutoCloseTimer();
        self.autoCloseTimer = setTimeout(function() {
          self.autoCloseTimer = null;
          self.close();
        }, self.autoCloseMs);
      }
    },

    // ── FS-031 : popup ย่อย "ตรวจสอบข้อมูลข้อความผิดพลาด" ─────────────────
    //
    //   spec §M : "เปิด popup ย่อยแสดงข้อมูลข้อความผิดพลาดโดยละเอียด
    //   (สาเหตุทางเทคนิค + เวลาที่เกิดเหตุ + เลขอ้างอิง) เพื่อให้ผู้ใช้ถ่ายภาพ/คัดลอก
    //   ส่งต่อทีมดูแลระบบ SBP"
    //
    //   ใช้ style ฝังในบรรทัดโดยตั้งใจ: Swal สร้าง DOM ไว้นอก component
    //   ทำให้ <style scoped> ของไฟล์นี้ใช้ไม่ได้
    openErrorDetail: function() {
      const item = this.failedItem;
      if (!item) return;

      const rowStyle = "padding:0.35rem 0;border-bottom:1px solid #ebedf2;";
      const labelStyle = "color:#74788d;font-size:0.85rem;";
      const valueStyle = "color:#48465b;word-break:break-all;";

      function row(label, value, testId) {
        return (
          '<div style="' + rowStyle + '">' +
          '<div style="' + labelStyle + '">' + label + "</div>" +
          '<div style="' + valueStyle + '" data-test="' + testId + '">' + value + "</div>" +
          "</div>"
        );
      }

      Swal.fire({
        title: "ข้อมูลข้อความผิดพลาด",
        html:
          '<div style="text-align:left;">' +
          row("เลขอ้างอิง (แจ้งทีมดูแลระบบ SBP)", this.failedReference, "fss-error-detail-reference") +
          row("เวลาที่เกิดเหตุ", this.formatDateTime(item.failedAt), "fss-error-detail-time") +
          row("ตัวแทนจำหน่ายที่ติดขัด", this.failedLabel, "fss-error-detail-agent") +
          row("ลำดับที่", String(item.seqNo), "fss-error-detail-seq") +
          row("สาเหตุทางเทคนิค", item.errorDetail || "ไม่ทราบสาเหตุ", "fss-error-detail-cause") +
          "</div>",
        type: "error",
        confirmButtonText: "ปิด",
      });
    },

    // เวลาแบบไทยอ่านง่าย — ค่าที่ได้จาก API เป็น ISO string
    formatDateTime: function(value) {
      if (!value) return "-";

      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);

      function pad(n) {
        return n < 10 ? "0" + n : String(n);
      }

      return (
        pad(date.getDate()) + "/" + pad(date.getMonth() + 1) + "/" + date.getFullYear() +
        " " + pad(date.getHours()) + ":" + pad(date.getMinutes()) + ":" + pad(date.getSeconds())
      );
    },

    // ── FS-031 ทางเลือกที่ 1 : ประมวลผลใหม่เริ่มจากรายที่ติดขัด ────────────
    //
    //   ไม่ต้องยืนยันซ้ำ — ทางเลือกนี้ไม่ทำให้อะไรหาย และผู้ใช้เพิ่งอ่าน
    //   คำอธิบายบน popup error มาแล้ว การถามซ้ำมีแต่จะทำให้กดผ่านโดยไม่อ่าน
    resumeFromFailure: function() {
      this.continueAfterFailure(false);
    },

    // ── FS-105 ทางเลือกที่ 2 : ข้ามการประมวลผลตัวแทนจำหน่ายที่ติดขัด ────────
    //
    //   แทนปุ่มเดิม "ยกเลิกและลบไฟล์ที่สร้างแล้ว" (ผู้ใช้สั่ง 15/10/2026)
    //   ไม่ลบอะไร จึงไม่ต้องยืนยันซ้ำเช่นเดียวกับทางเลือกที่ 1
    skipFailedAgent: function() {
      this.continueAfterFailure(true);
    },

    // ทั้งสองทางเปิดรอบใหม่บน batchId เดิม แล้ว poll ต่อ
    //   server ใส่รายการที่จบแล้วกลับเข้ามาให้ % และ log เดินต่อ ไม่เด้งกลับไป 0
    continueAfterFailure: function(skipFailed) {
      const self = this;

      if (!self.batchId || self.resuming || self.skipping) return;

      if (skipFailed) self.skipping = true;
      else self.resuming = true;

      axios
        .post(
          self.endpoints.resume,
          { batchId: self.batchId, skipFailed: skipFailed },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};

          if (!data.success) {
            Swal.fire("ทำงานต่อไม่ได้", data.message || "ไม่สามารถประมวลผลต่อได้", "warning");
            return;
          }

          // กลับเข้าโหมด "กำลังทำงาน" แล้ว poll ต่อด้วย batchId เดิม
          //   (ข้ามรายสุดท้ายของชุด server ปิดงานให้ทันที — poll รอบแรกจะเห็นสถานะจบเอง)
          self.state = "Running";
          self.finishedMessage = "";
          self.cancelRequested = false;
          self.consecutiveErrors = 0;
          self.agentCount = data.agentCount || self.agentCount;
          self.showModal();
          self.scheduleNextPoll();
        })
        .catch(function() {
          Swal.fire("เกิดข้อผิดพลาด", "สั่งประมวลผลต่อไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", "error");
        })
        .then(function() {
          self.resuming = false;
          self.skipping = false;
        });
    },

    // ── F13 (J2) : ดาวน์โหลดไฟล์ผลลัพธ์ ───────────────────────────────────
    //
    //   ต้องยิงด้วย axios พร้อม Authorization header — endpoint ป้องกันด้วย Bearer
    //   การใช้ <a href> ธรรมดาจะไม่แนบ token แล้วได้ 401
    //
    //   ใช้ responseType "blob" ไม่ใช่ "arraybuffer" เพื่อให้เบราว์เซอร์จัดการ
    //   หน่วยความจำเอง (ไฟล์ ZIP ของ 55 เอกสารอาจใหญ่หลายร้อย MB)
    downloadResult: function() {
      const self = this;

      if (!self.batchId || !self.endpoints.download || self.downloading) return;

      const url = self.endpoints.download.replace(
        "{jobId}",
        encodeURIComponent(self.batchId)
      );

      self.downloading = true;

      axios
        .get(url, {
          responseType: "blob",
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function(response) {
          const contentType = response.headers["content-type"] || "";

          // server ปฏิเสธ (ไฟล์หมดอายุ) จะคืน JSON ไม่ใช่ไฟล์
          if (contentType.indexOf("application/json") !== -1) {
            const reader = new FileReader();
            reader.onload = function() {
              let message = "ไม่พบไฟล์ที่ต้องการดาวน์โหลด";
              try {
                const parsed = JSON.parse(reader.result);
                if (parsed && parsed.message) message = parsed.message;
              } catch (e) {
                // ปล่อยข้อความเริ่มต้นไว้
              }
              Swal.fire("ดาวน์โหลดไม่ได้", message, "warning");
            };
            reader.readAsText(response.data);
            return;
          }

          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(response.data);
          link.download = self.resultFileName || "download.zip";
          link.click();

          // คืนหน่วยความจำให้เบราว์เซอร์ ไม่ปล่อยให้ค้างจนกว่าจะปิดแท็บ
          setTimeout(function() {
            window.URL.revokeObjectURL(link.href);
          }, 30000);
        })
        .catch(function() {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            "ดาวน์โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          self.downloading = false;
        });
    },

    // ── ยกเลิก ────────────────────────────────────────────────────────────
    //
    // FS-028 — ข้อความต้องตรงกับสิ่งที่ระบบทำจริง (ยืนยันจากโค้ดแล้ว):
    //   FormSummaryStockController.GroupSaveStart จับ OperationCanceledException
    //   -> SaveFormSummaryStockBatchCommandHandler.RollbackBatchAsync()
    //   -> soft delete "หัวเอกสาร" (FormSummaryStock_Header.IsDeleted = 1) ทุกใบของ BatchId นั้น
    //   ทุก query ของ module กรอง IsDeleted ออกอยู่แล้ว เอกสารจึงหายจากหน้ารายการ
    //   เปิดดู / พิมพ์ PDF / Export Excel ไม่ได้อีก และไม่มีหน้าจอไหนกู้คืนให้ได้
    //
    //   ของเดิมเขียนว่าเอกสาร "ยังอยู่ในระบบ" และให้ผู้ใช้ไปลบเอง
    //   = ผู้ใช้ตัดสินใจบนข้อมูลที่ผิด ทั้งที่เป็นการลบที่กู้ไม่ได้
    //
    // ⚠ popup ตัวนี้ใช้ร่วมกับงาน ZIP ด้วย และงาน ZIP ก็รายงาน item เป็น Created เหมือนกัน
    //   (ExportFormSummaryStockBatchZipCommandHandler) แต่ยกเลิกงาน ZIP ไม่ได้ลบเอกสารเลย
    //   จึงต้องแยกด้วย endpoint ที่ผู้เรียกส่งมา ไม่ใช่ดู createdCount อย่างเดียว
    //
    // ปุ่มที่โฟกัสต้องเป็น "ทำต่อ" (focusCancel) — Enter ต้องไม่ไปโดนตัวที่ลบข้อมูลทิ้ง
    //   บทเรียนเดียวกับ popup ออกจากหน้าสรุปผล (FS-019)
    //
    // ใช้ style ฝังในบรรทัดโดยตั้งใจ: Swal สร้าง DOM ไว้นอก component
    //   ทำให้ <style scoped> ของไฟล์นี้ใช้ไม่ได้
    requestCancel: function() {
      const self = this;

      const boxStyle =
        "text-align:left;border-radius:4px;" +
        "padding:0.75rem 0.9rem;margin-top:0.9rem;color:#48465b;";

      // งานสร้างเอกสารรวมเท่านั้นที่ยกเลิกแล้วลบเอกสารทิ้ง (H3 / RollbackBatchAsync)
      const jobKind = self.jobKindResolved;
      const isDocumentJob = jobKind === "document";

      let body;

      if (jobKind === "process") {
        // FS-033 — ขั้น "ประมวลผล" ยังไม่ได้บันทึกอะไรลงระบบเลย ไม่มีทั้งเอกสารและไฟล์ให้ทิ้ง
        //   ข้อความจึงต้องไม่มีคำที่สื่อว่าจะมีอะไรหาย (บทเรียนกลับด้านของ FS-028 —
        //   ตอนนั้นข้อความเบาเกินความจริง ตอนนี้ต้องไม่หนักเกินความจริง)
        body =
          '<div data-test="fss-cancel-note" style="' +
          boxStyle +
          'background-color:#f7f8fa;border:1px solid #ebedf2;">' +
          "ขั้นตอนนี้เป็นการคำนวณเพื่อแสดงผลเท่านั้น " +
          "<strong>ยังไม่มีการบันทึกเอกสารหรือไฟล์ใดลงระบบ</strong> — " +
          "ท่านจะยังอยู่ที่หน้าเลือก Filter และสั่งประมวลผลใหม่ได้ทันที" +
          "</div>";
      } else if (!isDocumentJob) {
        // งาน ZIP / Excel ตรวจ — ยกเลิกแล้วทิ้งแค่ไฟล์ที่ทำค้างอยู่ ไม่แตะเอกสารที่บันทึกไว้แล้ว
        body =
          '<div data-test="fss-cancel-note" style="' +
          boxStyle +
          'background-color:#f7f8fa;border:1px solid #ebedf2;">' +
          "ไฟล์ที่กำลังสร้างจะถูกทิ้ง — <strong>เอกสารที่บันทึกไว้แล้วไม่ได้รับผลกระทบ</strong> " +
          "สั่งสร้างไฟล์ใหม่ได้ตลอด" +
          "</div>";
      } else if (self.createdCount > 0) {
        body =
          '<div data-test="fss-cancel-delete-warning" style="' +
          boxStyle +
          "background-color:#fff0f4;border:1px solid #f8bfd0;" +
          'border-left:4px solid #fd397a;">' +
          "<strong>เอกสารที่สร้างไปแล้ว " +
          self.createdCount +
          " ฉบับ จะถูกลบออกจากระบบทั้งหมด</strong><br>" +
          "ลบแล้ว<strong>กู้คืนไม่ได้</strong> — เอกสารจะหายจากหน้ารายการเอกสาร " +
          "เปิดดู พิมพ์ PDF หรือ Export Excel ไม่ได้อีก<br>" +
          "หากยังต้องการเอกสารเหล่านี้ ต้องสั่งสร้างใหม่ทั้งชุด" +
          "</div>";
      } else {
        // ยังไม่มีเอกสารถูกสร้าง = ไม่มีอะไรให้ลบ ไม่ต้องขู่ผู้ใช้เกินจริง
        body =
          '<div data-test="fss-cancel-note" style="' +
          boxStyle +
          'background-color:#f7f8fa;border:1px solid #ebedf2;">' +
          "ยังไม่มีเอกสารถูกสร้างในชุดนี้ จึงไม่มีเอกสารที่ต้องลบ" +
          "</div>";
      }

      const willDelete = isDocumentJob && self.createdCount > 0;

      let cancelTitle = "ยืนยันยกเลิกการสร้างไฟล์";
      if (isDocumentJob) cancelTitle = "ยืนยันยกเลิกการสร้างเอกสาร";
      else if (jobKind === "process") cancelTitle = "ยืนยันยกเลิกการประมวลผล";

      Swal.fire({
        title: cancelTitle,
        html:
          '<div style="text-align:left;" data-test="fss-cancel-main">' +
          (isDocumentJob
            ? "ระบบจะหยุดหลังจากเอกสารฉบับที่กำลังทำอยู่เสร็จ"
            : "ระบบจะหยุดหลังจากรายการที่กำลังทำอยู่เสร็จ") +
          "</div>" +
          body,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: willDelete ? "ยืนยันยกเลิกและลบเอกสาร" : "ยืนยันยกเลิก",
        cancelButtonText: "ทำต่อ",
        // สีแดงของระบบ — ปุ่มที่ลบข้อมูลต้องไม่หน้าตาเหมือนปุ่มยืนยันทั่วไป
        //   งานที่ไม่ได้ลบอะไรใช้สีเริ่มต้นของ SweetAlert2 v8 ตามเดิม
        confirmButtonColor: willDelete ? "#fd397a" : "#3085d6",
        // ปุ่มที่โฟกัสอยู่คือปุ่มที่ Enter จะกดโดน จึงต้องเป็นตัวที่ไม่ทำให้ข้อมูลหาย
        focusCancel: true,
      }).then(function(result) {
        // SweetAlert2 v8 — ผลการยืนยันอยู่ที่ result.value
        if (!result.value) return;

        self.cancelRequested = true;

        axios
          .post(
            self.endpoints.cancel,
            { batchId: self.batchId },
            { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
          )
          .catch(function() {
            // ยกเลิกไม่สำเร็จก็ปล่อยให้ polling รายงานสถานะจริงต่อไป
            self.cancelRequested = false;
          });
      });
    },

    // ── ปิด ───────────────────────────────────────────────────────────────
    //   extra — ข้อมูลเพิ่มที่ผู้เรียกต้องรู้ (ผู้เรียกปัจจุบันยังรองรับ rolledBack:true ของ FS-031)
    close: function(extra) {
      this.clearTimer();
      this.clearDeferTimer();
      this.clearAutoCloseTimer();

      if (this.isShown && this.$refs.modalRoot) {
        $(this.$refs.modalRoot).modal("hide");
      }
      this.isShown = false;

      this.$emit("closed", Object.assign({
        batchId: this.batchId,
        state: this.state,
        jobKind: this.jobKindResolved,
        createdCount: this.createdCount,
      }, extra || {}));
    },

    reset: function() {
      this.clearTimer();
      this.clearDeferTimer();

      // FS-126
      this.resetElapsedTimer();
      this.showTimer = false;

      this.batchId = null;
      this.agentCount = 0;
      this.processedCount = 0;
      this.createdCount = 0;
      this.skippedCount = 0;
      this.failedCount = 0;
      this.percent = 0;
      this.currentAgentCode = "";
      this.currentAgentName = "";
      this.state = "Running";
      this.finishedMessage = "";
      this.items = [];
      this.cancelRequested = false;
      this.consecutiveErrors = 0;
      this.hasResultFile = false;
      this.resultFileName = "";
      this.downloading = false;

      // FS-031/FS-033
      this.resuming = false;
      this.skipping = false;
      this.isShown = false;
      this.jobKind = "";
      this.deferMs = 0;

      // FS-110
      this.clearAutoCloseTimer();
      this.autoCloseMs = 0;
    },

    clearTimer: function() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
  },
  // ออกจากหน้าไปโดยที่ยัง poll อยู่ ต้องหยุด timer ไม่งั้นมันยิง API ต่อไปเรื่อย ๆ
  beforeDestroy: function() {
    this.clearTimer();
    this.clearDeferTimer();
    this.clearAutoCloseTimer();
    this.pauseElapsedTimer();
  },
};
</script>

<style scoped>
/* FS-126 — ตัวนับเวลามุมขวาบนของหัว popup (modal-header เป็น flex : title ซ้าย · ตัวนับชิดขวา) */
.fss-progress__timer {
  margin-left: auto;
  align-self: center;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.fss-progress__headline {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
}

.fss-progress__counter {
  font-size: 1.15rem;
  font-weight: 600;
}

.fss-progress__percent {
  font-size: 1.35rem;
  font-weight: 700;
}

.fss-progress__bar {
  height: 1.25rem;
}

.fss-progress__current {
  margin-top: 0.75rem;
  color: #5867dd;
}

.fss-progress__stats {
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
}

.fss-progress__stat {
  margin-right: 1.25rem;
  font-size: 0.95rem;
}

.fss-progress__stat--ok {
  color: #0abb87;
}

.fss-progress__stat--skip {
  color: #74788d;
}

.fss-progress__stat--fail {
  color: #fd397a;
}

.fss-progress__log {
  margin-top: 1rem;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ebedf2;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  background-color: #fafbfc;
}

.fss-progress__row {
  display: flex;
  align-items: baseline;
  padding: 0.2rem 0;
  font-size: 0.92rem;
}

.fss-progress__row--ok .fss-progress__icon {
  color: #0abb87;
}

.fss-progress__row--skip .fss-progress__icon {
  color: #a7abc3;
}

.fss-progress__row--fail .fss-progress__icon {
  color: #fd397a;
}

.fss-progress__icon {
  width: 1.4rem;
  font-weight: 700;
}

.fss-progress__seq {
  width: 2.2rem;
  color: #74788d;
}

.fss-progress__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fss-progress__note {
  margin-left: 0.75rem;
  color: #74788d;
  white-space: nowrap;
}

.fss-progress__empty {
  color: #a7abc3;
  text-align: center;
  padding: 1rem 0;
}

.fss-progress__result {
  margin-top: 1rem;
  margin-bottom: 0;
}

.fss-progress__cancelling {
  margin-top: 0.75rem;
  color: #ffb822;
}

/* ── FS-031 : Popup Error (พื้นส้ม) ตาม spec §M ──────────────────────────── */
/* spec ระบุสีพื้นส้มเพื่อให้แยกจาก popup ความคืบหน้าปกติได้ทันทีตั้งแต่เหลือบตามอง
   ใช้โทนส้มของธีม (#ffb822) ไม่ใช่ส้มลอย ๆ เพื่อให้เข้ากับหน้าจออื่นของระบบ */
.fss-progress--error {
  background-color: #fff4e0;
  border: 1px solid #ffd79a;
}

.fss-progress--error .modal-header {
  background-color: #ffe7c2;
  border-bottom-color: #ffd79a;
  color: #8a5a00;
}

.fss-progress--error .modal-footer {
  border-top-color: #ffd79a;
}

.fss-progress__error {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border: 1px solid #ffd79a;
  border-left: 4px solid #ffb822;
  border-radius: 4px;
  background-color: #fffaf0;
}

.fss-progress__error-stat {
  color: #0abb87;
  font-weight: 600;
}

.fss-progress__error-agent {
  margin-top: 0.35rem;
  color: #fd397a;
  font-weight: 600;
}

.fss-progress__error-ref {
  margin-top: 0.35rem;
  color: #74788d;
  font-size: 0.9rem;
}

.fss-progress__error-ref code {
  color: #48465b;
  background-color: #f2f3f8;
  padding: 1px 6px;
  border-radius: 3px;
}

/* FS-105 — โทนฟ้าตาม mockup §M (.btn-error-log) ผู้ใช้สั่ง 15/10/2026 */
.fss-progress__error-detail-btn {
  margin-top: 0.75rem;
  background-color: #ebf3fc;
  color: #3b7ddd;
  border: 1px solid #3b7ddd;
  font-weight: 600;
}

.fss-progress__error-detail-btn:hover,
.fss-progress__error-detail-btn:focus {
  background-color: #d5e5f8;
  color: #3b7ddd;
}

.fss-progress__error-choose {
  margin-top: 0.9rem;
  color: #48465b;
  font-weight: 600;
}

/* ── FS-031 : กล่องทางเลือก 2 ข้อ — สี/ระยะตาม mockup §M ─────────────────── */
.fss-error-action {
  display: block;
  width: 100%;
  text-align: left;
  border: 2px solid #e0e7ef;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 8px 0;
  background-color: #ffffff;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.fss-error-action:disabled {
  opacity: 0.6;
  cursor: default;
}

.fss-error-action__title {
  display: block;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.fss-error-action__title i {
  margin-right: 8px;
}

.fss-error-action__desc {
  display: block;
  margin-left: 24px;
  color: #74788d;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: normal;
}

.fss-error-action--resume {
  border-color: #28a745;
  background-color: #f1faf3;
}

.fss-error-action--resume:hover:not(:disabled) {
  background-color: #daf3df;
}

.fss-error-action--resume .fss-error-action__title {
  color: #217346;
}

/* FS-105 — "ข้าม" ไม่ได้ลบอะไร จึงไม่ใช้สีแดงของปุ่มเดิม (ยกเลิกและลบ) ใช้สีหลักของระบบแทน */
.fss-error-action--skip {
  border-color: #5867dd;
  background-color: #f4f5fd;
}

.fss-error-action--skip:hover:not(:disabled) {
  background-color: #e3e6fa;
}

.fss-error-action--skip .fss-error-action__title {
  color: #3f4bb8;
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockProgressModal.vue